import React, { cloneElement, useEffect, useState, useCallback } from "react"
import type { ReactElement, ReactNode } from "react"
import { useTooltip, TooltipPopup } from "@reach/tooltip"
import type { Position } from "@reach/tooltip"
import Portal from "../portal"

export type TooltipProps = {
  children: ReactElement
  label: ReactNode
  position?: Position
  showArrow?: boolean
}

const bottom: Position = (triggerRect, tooltipRect) => {
  if (!triggerRect) {
    return { left: 0, top: 0 }
  }

  // If tooltipRect is not available yet, don't position correctly
  // Return null position to indicate tooltip isn't ready
  if (!tooltipRect) {
    return { left: -9999, top: -9999 }
  }

  const triggerCenter = triggerRect.left + triggerRect.width / 2
  const left = triggerCenter - tooltipRect.width / 2
  const maxLeft = document.documentElement.clientWidth - tooltipRect.width - 2

  return {
    left: Math.min(Math.max(2, left), maxLeft) + window.scrollX,
    top: triggerRect.bottom + 8 + window.scrollY
  }
}

export const top: Position = (triggerRect, tooltipRect) => {
  if (!triggerRect) {
    return { left: 0, top: 0 }
  }

  // If tooltipRect is not available yet, hide tooltip off-screen
  // to prevent visible jumping
  if (!tooltipRect) {
    return { left: -9999, top: -9999 }
  }

  const triggerCenter = triggerRect.left + triggerRect.width / 2
  const left = triggerCenter - tooltipRect.width / 2
  const maxLeft = document.documentElement.clientWidth - tooltipRect.width - 2

  return {
    left: Math.min(Math.max(2, left), maxLeft) + window.scrollX,
    top: triggerRect.top - tooltipRect.height - 8 + window.scrollY
  }
}

function Tooltip({ children, label, position = bottom, showArrow = true }: TooltipProps) {
  const [trigger, tooltip] = useTooltip()
  const { isVisible, triggerRect } = tooltip
  const [isPositioned, setIsPositioned] = useState(false)

  // Track when tooltipRect becomes available
  const positionWithTracking: Position = useCallback(
    (triggerRect, tooltipRect) => {
      const result = position(triggerRect, tooltipRect)

      // Once tooltipRect is available and position is calculated, mark as positioned
      if (tooltipRect && triggerRect) {
        // Use requestAnimationFrame to ensure DOM update happens first
        requestAnimationFrame(() => {
          setIsPositioned(true)
        })
      } else {
        setIsPositioned(false)
      }

      return result
    },
    [position]
  )

  // Reset when tooltip becomes invisible
  useEffect(() => {
    if (!isVisible) {
      setIsPositioned(false)
    }
  }, [isVisible])

  // Control tooltip visibility via CSS when positioned
  useEffect(() => {
    if (!isVisible) return

    const updateTooltipVisibility = () => {
      const tooltipElement = document.querySelector("[data-reach-tooltip]") as HTMLElement | null
      if (tooltipElement) {
        const rect = tooltipElement.getBoundingClientRect()
        // If tooltip is off-screen (at -9999) or not yet positioned, hide it
        if (rect.left <= -9000 || !isPositioned) {
          tooltipElement.style.visibility = "hidden"
        } else {
          tooltipElement.style.visibility = "visible"
        }
      }
    }

    // Check visibility after positioning state changes
    const timeoutId = setTimeout(updateTooltipVisibility, 0)
    requestAnimationFrame(updateTooltipVisibility)

    return () => clearTimeout(timeoutId)
  }, [isVisible, isPositioned])

  return (
    <React.Fragment>
      {cloneElement(children, trigger)}

      {isVisible && showArrow && (
        <Portal>
          <div
            className="tooltip-arrow"
            style={{
              position: "absolute",
              left:
                triggerRect ? triggerRect.left - 10 + triggerRect.width / 2 : undefined,
              top: triggerRect ? triggerRect.bottom + window.scrollY : undefined,
              width: 0,
              height: 0,
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",
              borderBottom: "10px solid #000000"
            }}
          />
        </Portal>
      )}
      {isVisible && (
        <TooltipPopup
          {...tooltip}
          label={label}
          position={positionWithTracking}
        />
      )}
    </React.Fragment>
  )
}

export default Tooltip
